<template>
  <div>
    <v-container class="page-title page-title--advanced">
      <v-row>
        <v-col class="page-title__title-box">
          <h4 class="page-title__title">
            {{ $t("heading.instance.advanced.wordpressMultisite.title") }}
          </h4>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col cols="12" class="pb-2">
          <v-card elevation="4">
            <v-card-text class="text--gray-darken2">
              <template v-if="isConverted">
                <h6>
                  {{
                    $t(
                      "heading.instance.advanced.wordpressMultisite.convertedTitle"
                    )
                  }}
                </h6>
                <p class="mb-0 mt-1">
                  {{
                    $t(
                      "heading.instance.advanced.wordpressMultisite.convertedHint"
                    )
                  }}
                </p>
              </template>

              <template v-else-if="isInProgress">
                <div class="d-flex align-center">
                  <v-progress-circular
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                    class="mr-3"
                  />
                  <span>{{
                    $t("heading.instance.advanced.wordpressMultisite.inProgress")
                  }}</span>
                </div>
              </template>

              <template v-else>
                <h6>
                  {{ $t("heading.instance.advanced.wordpressMultisite.title") }}
                </h6>
                <p class="mb-0 mt-1">
                  {{
                    $t(
                      "heading.instance.advanced.wordpressMultisite.description"
                    )
                  }}
                </p>

                <alert-sm
                  v-if="isFailed"
                  :class="'alert-sm--error mt-3'"
                  :text="
                    $t('heading.instance.advanced.wordpressMultisite.failed')
                  "
                />

                <div class="mt-4">
                  <strong>{{
                    $t(
                      "heading.instance.advanced.wordpressMultisite.warningsTitle"
                    )
                  }}</strong>
                  <ul class="mt-2 mb-0">
                    <li>
                      {{
                        $t(
                          "heading.instance.advanced.wordpressMultisite.warningIrreversible"
                        )
                      }}
                    </li>
                    <li>
                      {{
                        $t(
                          "heading.instance.advanced.wordpressMultisite.warningBackup"
                        )
                      }}
                    </li>
                    <li>
                      {{
                        $t(
                          "heading.instance.advanced.wordpressMultisite.warningBlockedTitle"
                        )
                      }}
                      <ul>
                        <li v-for="key in blockedFeatures" :key="key">
                          {{
                            $t(
                              `heading.instance.advanced.wordpressMultisite.${key}`
                            )
                          }}
                        </li>
                      </ul>
                    </li>
                    <li>
                      {{
                        $t(
                          "heading.instance.advanced.wordpressMultisite.warningSubsites"
                        )
                      }}
                    </li>
                  </ul>
                </div>

                <v-btn
                  elevation="0"
                  class="font-weight-bold v-btn--default mt-4"
                  :loading="submitting"
                  :disabled="submitting"
                  @click="openConfirm"
                >
                  {{
                    isFailed
                      ? $t(
                          "heading.instance.advanced.wordpressMultisite.tryAgain"
                        )
                      : $t(
                          "heading.instance.advanced.wordpressMultisite.convert"
                        )
                  }}
                </v-btn>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
      transition="custom-dialog-transition"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()" />
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
      />
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/apis/Api";
import ActionModalMixin from "../../../mixins/ActionModalMixin";
import AlertSm from "../../../components/alerts/AlertSm.vue";
import BasicModal from "../../../components/modal/BasicModal.vue";

export default {
  name: "WordpressMultisite",
  components: {
    AlertSm,
    BasicModal,
  },
  mixins: [ActionModalMixin],
  props: {
    instance: Object,
  },
  data() {
    return {
      applicationId: this.$route.params.id,
      submitting: false,
      pollTimer: null,
      // Local copy of the async status, kept fresh by polling and websocket.
      status: this.instance?.async_status?.multisite_convert || null,
      converted: !!this.instance?.details?.multisite,
      blockedFeatures: [
        "blockedChangeDomain",
        "blockedPrimaryDomain",
        "blockedStaging",
        "blockedPush",
        "blockedCopy",
        "blockedPreview",
        "blockedTemplate",
        "blockedThemeDelete",
      ],
    };
  },
  computed: {
    isConverted() {
      return this.converted;
    },
    isInProgress() {
      return this.status === "pending" || this.status === "in_progress";
    },
    isFailed() {
      return this.status === "failed";
    },
  },
  mounted() {
    this.$root.$on(
      "wordpress-multisite-status-changed",
      this.onStatusChanged
    );
    this.maybePoll();
  },
  beforeDestroy() {
    this.$root.$off(
      "wordpress-multisite-status-changed",
      this.onStatusChanged
    );
    this.stopPolling();
  },
  methods: {
    openConfirm() {
      if (this.submitting) {
        return;
      }
      this.resetModal();
      this.modalOptions.title = this.$t(
        "heading.instance.advanced.wordpressMultisite.confirmTitle"
      );
      this.modalOptions.message = this.$t(
        "heading.instance.advanced.wordpressMultisite.confirmBody"
      );
      this.modalOptions.icon = "$alertwarning";
      this.modalOptions.color = "warning";
      this.modalOptions.persistent = true;
      this.modalOptions.buttons.unshift({
        label: this.$t(
          "heading.instance.advanced.wordpressMultisite.confirm"
        ),
        color: "warning",
        onclick: () => {
          this.modalClose();
          this.convert();
        },
      });
      this.modalOptions.open = true;
    },
    async convert() {
      this.submitting = true;
      try {
        await Api.post(`/instances/${this.applicationId}/wordpress/multisite`);
        this.status = "pending";
        this.maybePoll();
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.submitting = false;
      }
    },
    async refreshInstance() {
      const { data } = await Api.get(`/instances/${this.applicationId}`);
      const app = data.data;
      this.status = app.async_status?.multisite_convert || null;
      this.converted = !!app.details?.multisite;
      // Keep the shared instance in sync so the sidebar and top bar badge update.
      if (this.instance) {
        if (this.instance.async_status) {
          this.$set(this.instance.async_status, "multisite_convert", this.status);
        }
        this.$set(this.instance, "details", {
          ...(this.instance.details || {}),
          multisite: this.converted,
        });
      }
    },
    async onStatusChanged(instance) {
      if (String(instance.id) !== String(this.applicationId)) {
        return;
      }
      await this.refreshAndNotify();
    },
    async refreshAndNotify() {
      const wasInProgress = this.isInProgress;
      try {
        await this.refreshInstance();
      } catch (error) {
        this.stopPolling();
        return;
      }
      if (this.isInProgress) {
        return;
      }
      this.stopPolling();
      if (!wasInProgress) {
        return;
      }
      if (this.converted) {
        this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t(
            "heading.instance.advanced.wordpressMultisite.convertSuccess"
          ),
        });
      } else {
        this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: this.$t(
            "heading.instance.advanced.wordpressMultisite.failed"
          ),
        });
      }
    },
    maybePoll() {
      this.stopPolling();
      if (!this.isInProgress) {
        return;
      }
      this.pollTimer = setInterval(() => {
        this.refreshAndNotify();
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
  watch: {
    "modalOptions.open": function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
};
</script>
